import { useTranslations } from '@vocab/react';
import { Box, IconRenderer, Text, TextLinkButton } from 'braid-design-system';
import type { FC } from 'react';

import type { ResponseErrorReason } from '../graphql/hirer-types.generated';

import translations from './.vocab';

import * as styles from './PlacementStatusLink.css';

interface PlacementStatusLinkProps {
  openJobPlacementDialog: () => void;
  placementV2:
    | {
        __typename?: 'JobPlacementV2ResponseSuccess';
        result: {
          __typename?: 'JobPlacementV2';
          canShowPlacementPrompt: boolean;
          status?: JobPlacementStatus | null;
        };
      }
    | { __typename?: 'ResponseError'; error: ResponseErrorReason };
  trackJobPlacementStatusLinkClickedEvent: (
    hiringStatus:
      | 'Hiring status unavailable'
      | 'Role filled'
      | 'Role not filled'
      | 'Hiring in progress',
  ) => void;
}

export const jobPlacementStatus = {
  IN_PROGRESS: 'IN_PROGRESS',
  FILLED: 'FILLED',
  NOT_FILLED: 'NOT_FILLED',
} as const;

export type JobPlacementStatus =
  | (typeof jobPlacementStatus)[keyof typeof jobPlacementStatus]
  | undefined;

export const PlacementStatusLink: FC<PlacementStatusLinkProps> = ({
  openJobPlacementDialog,
  placementV2,
  trackJobPlacementStatusLinkClickedEvent,
}) => {
  const { t } = useTranslations(translations);

  if (placementV2.__typename !== 'JobPlacementV2ResponseSuccess') {
    return (
      <Text size="small" tone="secondary">
        <TextLinkButton
          weight="weak"
          onClick={() => {
            openJobPlacementDialog();
            trackJobPlacementStatusLinkClickedEvent(
              'Hiring status unavailable',
            );
          }}
          icon={<IndicatorIcon tone="critical" />}
        >
          {t('Hiring status unavailable')}
        </TextLinkButton>
      </Text>
    );
  }

  if (placementV2.result.status === 'IN_PROGRESS') {
    return (
      <Text size="small" tone="secondary">
        <TextLinkButton
          weight="weak"
          onClick={() => {
            openJobPlacementDialog();
            trackJobPlacementStatusLinkClickedEvent('Hiring in progress');
          }}
          icon={<IndicatorIcon tone="caution" animation />}
        >
          {t('Hiring in progress')}
        </TextLinkButton>
      </Text>
    );
  }

  if (placementV2.result.status === 'FILLED') {
    return (
      <Text size="small" tone="secondary">
        <TextLinkButton
          weight="weak"
          onClick={() => {
            openJobPlacementDialog();
            trackJobPlacementStatusLinkClickedEvent('Role filled');
          }}
          icon={<IndicatorIcon tone="positive" />}
        >
          {t('Role filled')}
        </TextLinkButton>
      </Text>
    );
  }

  if (placementV2.result.status === 'NOT_FILLED') {
    return (
      <Text size="small" tone="secondary">
        <TextLinkButton
          weight="weak"
          onClick={() => {
            openJobPlacementDialog();
            trackJobPlacementStatusLinkClickedEvent('Role not filled');
          }}
          icon={<IndicatorIcon tone="critical" />}
        >
          {t('Role not filled')}
        </TextLinkButton>
      </Text>
    );
  }

  return null;
};

const IndicatorIcon = ({
  tone,
  animation,
}: {
  tone: 'caution' | 'positive' | 'critical';
  animation?: boolean;
}) => (
  <IconRenderer>
    {({ className }) => (
      <Box className={[styles.indicatorContainer, className]}>
        <Box
          className={`${styles.indicator[tone]} ${
            animation ? styles.pulsingAnimation[tone] : ''
          }`}
        />
      </Box>
    )}
  </IconRenderer>
);
